import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import ReactGA from "react-ga";
import { sendPageView } from "lib/googleAnalytics/page";
import log from "loglevel";
import { Container, Row, Col } from "react-bootstrap";
import {
  decodeJwtAction,
  getPermissionsAction,
  getRolePermissionsAction,
  getRolesAction,
} from "store/actions/actionCreators";
import DashboardSidebar from "components/sidebar/DashboardSidebar";
import MainNav from "components/nav/MainNav";
import PermissionsCard from "components/dashboard/permissions/PermissionsCard";
import RolePermissionsCard from "components/dashboard/permissions/RolePermissionsCard";
import {
  getPermissionsReducerResponse,
  getRolePermissionsReducerResponse,
  getJwtReducerResponse,
} from "store/selectors";
import { RolePermission, Permission, JWT } from "types";
import { administratorRoleId } from "settings";
import * as routes from "routes";
import {
  Breadcrumb,
  BreadcrumbData,
  InfoTableCard,
  Page,
} from "@avlino-inc/alenza-react";
import { faFlagAlt } from "@fortawesome/pro-light-svg-icons/faFlagAlt";
import { faUserTag } from "@fortawesome/pro-light-svg-icons/faUserTag";

const DashboardPermissionsPage = ({ authed }: { authed: Promise<boolean> }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const permissionsData: Permission[] = useSelector((state) =>
    getPermissionsReducerResponse(state, "data")
  );
  const rolePermissionsData: RolePermission[] = useSelector((state) =>
    getRolePermissionsReducerResponse(state, "data")
  );
  const jwtPayloadData: JWT = useSelector((state) =>
    getJwtReducerResponse(state, "data")
  );

  const isAdmin = (jwtPayload: JWT, validRoleId: number) => {
    return jwtPayload && jwtPayload.role_id === validRoleId;
  };

  const isAvlino = (jwtPayload: JWT) => {
	return jwtPayload?.email?.endsWith('@avlino.com')
  }

  useEffect(() => {
    if (authed) {
      authed.then((allow) => {
        log.info("Allow: " + allow);
        if (allow) {
          log.info("Authenticated. Stay on page...");
        } else {
          log.info("Redirect back...");
          history.push(routes.LOGIN);
        }
      });
    } else {
      history.push(routes.LOGIN);
    }
  }, [authed, history]);

  useEffect(() => {
    log.info("DashboardMainPage useEffect()...");
    dispatch(decodeJwtAction({}));
    dispatch(getPermissionsAction({}));
    dispatch(getRolePermissionsAction({}));
    dispatch(getRolesAction({}));
  }, [dispatch]);

  /**
   * Google Analytics
   */
  useEffect(() => {
    sendPageView(location.pathname + location.search);
  }, [location]);
  
  const breadcrumbItems = useMemo<BreadcrumbData[]>(
    () => [
      {
        url: routes.HOME,
        label: "Command Center",
      },
      {
        url: routes.DASHBOARD_ADMIN,
        label: "Admin Dashboard",
      },
      {
        url: routes.DASHBOARD_ROLES_PERMISSIONS,
        label: "Roles & Permissions",
      },
    ],
    []
  );

  return (
    <div id="DashboardPermissionsPage">
      <MainNav
        first_name={jwtPayloadData && jwtPayloadData.first_name}
        last_name={jwtPayloadData && jwtPayloadData.last_name}
        username={jwtPayloadData && jwtPayloadData.username}
        email={jwtPayloadData && jwtPayloadData.email}
      />
      <div style={{ display: "flex" }}>
        <DashboardSidebar
          isAvlino={isAvlino(jwtPayloadData)}
          isAdmin={isAdmin(jwtPayloadData, administratorRoleId)}
        />
        <main id="page-wrap">
          <Container fluid={true} style={{ marginBottom: "100px" }}>
            <Page.Header>
              <Breadcrumb items={breadcrumbItems} onClickItem={history.push} />
            </Page.Header>
            <Row>
              <Col xs={12} xl={6}>
                <InfoTableCard center icon={faFlagAlt} color="bright-blue">
                  <PermissionsCard permissions={permissionsData} />
                </InfoTableCard>
                <br />
              </Col>
              <Col xs={12} xl={6}>
                <InfoTableCard center icon={faUserTag} color="eggplant">
                  <RolePermissionsCard rolePermissions={rolePermissionsData} />
                </InfoTableCard>
                <br />
              </Col>
            </Row>
          </Container>
        </main>
      </div>
    </div>
  );
};

export default DashboardPermissionsPage;
